/**
 * Primitives of the lock pages. They extend the token pages' night system (Page, Panel, Segmented, IconBtn, the moon surface) with the lock
 * vocabulary: what a colour means (ice-cyan sealed, amber date passed or near, violet released in steps, slate finished, coral irreversible),
 * a status pill, a figure, an address line with copy and explorer links, and the verified-source badge.
 */
import Link from 'next/link'
import { useCallback, useEffect, useRef, useState } from 'react'
import styled, { css } from 'styled-components'
import { codeUrl, addressUrl, sourcifyUrl } from './config'
import { shortAddress, checksum } from './format'
import type { LockPhase, LockSummary } from './types'
import { sealToneOf } from './logic/certificate'
import type { SealTone } from './art/sealSvg'
import { Icon } from '../TokenPages/icons'
import { DISPLAY, IconBtn, MONO, Num, Panel, SectionTitle, Segmented, SegBtn, Skel, Page, PrimaryCta, GhostCta, SoftCta, Pill, fmtDate, useNow } from '../TokenPages/ui'

export { DISPLAY, IconBtn, MONO, Num, Panel, SectionTitle, Segmented, SegBtn, Skel, Page, PrimaryCta, GhostCta, SoftCta, Pill, fmtDate, useNow }

/** Meaning of colour on the lock pages. One colour, one meaning; the page never uses a colour for decoration. */
export const TONE: Record<SealTone | 'danger' | 'ok', string> = {
  locked: '#7fd4e8', // sealed, locked, verified
  soon: '#f5b453', // the date is near
  due: '#f5b453', // the date has passed and the owner can act
  vesting: '#a99bff', // released in steps
  released: '#a9b4dc', // finished
  danger: '#ff6b7a', // cannot be undone
  ok: '#39d98a', // a tick on something verified
}

export const PHASE_LABEL: Record<SealTone, string> = {
  locked: 'Locked',
  soon: 'Unlocking soon',
  vesting: 'Releasing',
  due: 'Ready to unlock',
  released: 'Unlocked',
}
export const toneOfLock = (l: Pick<LockSummary, 'phase' | 'soon'>): SealTone => sealToneOf(l as LockSummary)
export const phaseLabel = (l: Pick<LockSummary, 'phase' | 'soon'>) => PHASE_LABEL[toneOfLock(l)]

const pillColors = (tone: string) => css`
  color: ${tone};
  border: 1px solid ${tone}66;
  background: ${tone}1f;
`
export const StatusPill = styled.span<{ $tone: SealTone | 'danger' | 'ok' | 'muted' }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 26px;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 12.5px;
  line-height: 16px;
  font-weight: 700;
  white-space: nowrap;
  ${({ $tone }) => pillColors($tone === 'muted' ? '#9fb4ec' : TONE[$tone])}
`
export function LockStatus({ lock }: { lock: Pick<LockSummary, 'phase' | 'soon'> }) {
  const t = toneOfLock(lock)
  const icon = lock.phase === 'released' ? 'check' : lock.phase === 'due' ? 'clock' : lock.phase === 'vesting' ? 'layers' : 'lock'
  return (
    <StatusPill $tone={t} data-testid="lock-status" data-phase={lock.phase} data-soon={lock.soon ? 'true' : 'false'}>
      <Icon name={icon} size={14} strokeWidth={2.2} />
      {PHASE_LABEL[t]}
    </StatusPill>
  )
}

/** A small caption over a figure: label above, value below, optional sub line. */
export const FigureBox = styled.div`
  display: grid;
  align-content: start;
  gap: 4px;
  min-width: 0;
`
export const FigLabel = styled.span`
  font-size: 12px;
  line-height: 16px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.textSubtle};
`
export const FigValue = styled.span<{ $tone?: string; $size?: number }>`
  font-family: ${DISPLAY};
  font-weight: 800;
  font-size: ${({ $size }) => $size ?? 28}px;
  line-height: 1.1;
  letter-spacing: -0.01em;
  color: ${({ $tone, theme }) => $tone ?? theme.colors.text};
  overflow-wrap: anywhere;
`
export const FigSub = styled.span`
  font-size: 13px;
  line-height: 18px;
  color: ${({ theme }) => theme.colors.textSubtle};
`
export function Figure({ label, value, sub, tone, size, testid }: { label: string; value: React.ReactNode; sub?: React.ReactNode; tone?: string; size?: number; testid?: string }) {
  return (
    <FigureBox data-testid={testid}>
      <FigLabel>{label}</FigLabel>
      <FigValue data-fig-value="" $tone={tone} $size={size}>
        {value}
      </FigValue>
      {sub ? <FigSub>{sub}</FigSub> : null}
    </FigureBox>
  )
}

export const Hairline = styled.hr`
  border: 0;
  height: 1px;
  margin: 0;
  background: ${({ theme }) => theme.colors.cardBorder};
`

/** A definition list as label / value rows that stay readable at 360 px (the value wraps, the label never does). */
export const Dl = styled.dl`
  display: grid;
  margin: 0;
  gap: 0;
`
export const DlRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 2px;
  padding: 12px 0;
  border-top: 1px solid ${({ theme }) => theme.colors.cardBorder};
  &:first-child {
    border-top: 0;
  }
  dt {
    font-size: 13px;
    line-height: 18px;
    color: ${({ theme }) => theme.colors.textSubtle};
  }
  dd {
    margin: 0;
    min-width: 0;
    font-size: 15px;
    line-height: 22px;
    color: ${({ theme }) => theme.colors.text};
    overflow-wrap: anywhere;
  }
  ${({ theme }) => theme.mediaQueries.sm} {
    grid-template-columns: 160px minmax(0, 1fr);
    gap: 16px;
    align-items: baseline;
  }
`

export function useCopy() {
  const [done, setDone] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout>>()
  useEffect(() => () => clearTimeout(timer.current), [])
  const copy = useCallback((text: string) => {
    const ok = () => {
      setDone(true)
      clearTimeout(timer.current)
      timer.current = setTimeout(() => setDone(false), 1600)
    }
    try {
      if (navigator.clipboard?.writeText) navigator.clipboard.writeText(text).then(ok, ok)
      else {
        const ta = document.createElement('textarea')
        ta.value = text
        ta.style.position = 'fixed'
        ta.style.opacity = '0'
        document.body.appendChild(ta)
        ta.select()
        document.execCommand('copy')
        document.body.removeChild(ta)
        ok()
      }
    } catch {
      /* the browser refused: nothing to show */
    }
  }, [])
  return { done, copy }
}

export const CopyBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 44px;
  min-width: 44px;
  padding: 0 12px;
  border-radius: 12px;
  cursor: pointer;
  font: inherit;
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textSubtle};
  background: rgba(5, 7, 19, 0.5);
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  &:hover {
    color: ${({ theme }) => theme.colors.text};
    border-color: ${({ theme }) => theme.colors.primary};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`
export function CopyButton({ text, label = 'Copy', testid }: { text: string; label?: string; testid?: string }) {
  const { done, copy } = useCopy()
  return (
    <CopyBtn type="button" onClick={() => copy(text)} aria-label={done ? 'Copied' : `${label}`} data-testid={testid}>
      <Icon name={done ? 'check' : 'copy'} size={16} />
      {done ? 'Copied' : label}
    </CopyBtn>
  )
}

const AddrRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  min-width: 0;
`
export const MonoText = styled.span`
  font-family: ${MONO};
  font-size: 14px;
  letter-spacing: -0.01em;
  overflow-wrap: anywhere;
`
const LinkOut = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 44px;
  padding: 0 12px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 700;
  text-decoration: none;
  color: ${({ theme }) => theme.colors.primary};
  background: rgba(127, 212, 232, 0.08);
  border: 1px solid rgba(127, 212, 232, 0.28);
  &:hover {
    background: rgba(127, 212, 232, 0.16);
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`
export { LinkOut }

/** An address: short or full, with copy and a BscScan link. Always shown checksummed, never lowercased. */
export function AddressLine({ address, full, label, testid }: { address: string; full?: boolean; label?: string; testid?: string }) {
  const a = checksum(address)
  return (
    <AddrRow data-testid={testid}>
      <MonoText title={a}>{full ? a : shortAddress(a)}</MonoText>
      <CopyButton text={a} label={label ?? 'Copy'} />
      <LinkOut href={addressUrl(a)} target="_blank" rel="noopener noreferrer" aria-label="Open the address on BscScan">
        BscScan <Icon name="external" size={14} />
      </LinkOut>
    </AddrRow>
  )
}

const BadgeWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`
const BadgeMain = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 32px;
  padding: 0 12px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 700;
  color: ${TONE.ok};
  background: rgba(57, 217, 138, 0.1);
  border: 1px solid rgba(57, 217, 138, 0.34);
`
/** "Source verified": exact match on Sourcify, and the same address on BscScan's code tab. The claim is the verification the chain tools can show, nothing more. */
export function VerifiedBadge({ address, testid }: { address: string; testid?: string }) {
  const a = checksum(address)
  return (
    <BadgeWrap data-testid={testid ?? 'verified-badge'}>
      <BadgeMain title="The contract's source code matches its deployed bytecode exactly (Sourcify, exact match)">
        <Icon name="shieldCheck" size={16} strokeWidth={2} />
        Source verified
      </BadgeMain>
      <LinkOut href={codeUrl(a)} target="_blank" rel="noopener noreferrer">
        Code on BscScan <Icon name="external" size={14} />
      </LinkOut>
      <LinkOut href={sourcifyUrl(a)} target="_blank" rel="noopener noreferrer">
        Sourcify <Icon name="external" size={14} />
      </LinkOut>
    </BadgeWrap>
  )
}

/** A back link in the page's own style. */
export const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 44px;
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textSubtle};
  text-decoration: none;
  &:hover {
    color: ${({ theme }) => theme.colors.text};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
    border-radius: 8px;
  }
`

export const Stack = styled.div<{ $gap?: number }>`
  display: grid;
  gap: ${({ $gap }) => $gap ?? 16}px;
  min-width: 0;
`

/** The two-column certificate grid: seven twelfths of content, five of proof, one column on a phone and a tablet. */
export const TwoCol = styled.div`
  display: grid;
  gap: 16px;
  align-items: start;
  grid-template-columns: minmax(0, 1fr);
  ${({ theme }) => theme.mediaQueries.lg} {
    gap: 20px;
    grid-template-columns: minmax(0, 7fr) minmax(0, 5fr);
  }
`

export const PanelTitle = ({ icon, children }: { icon?: React.ComponentProps<typeof Icon>['name']; children: React.ReactNode }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
    {icon ? <Icon name={icon} size={20} style={{ color: TONE.locked }} /> : null}
    <SectionTitle as="h2" style={{ fontSize: 20, lineHeight: '26px' }}>
      {children}
    </SectionTitle>
  </div>
)

/** A call to action that is a Next link: the same look as PrimaryCta / GhostCta, client-side navigation. */
export function CtaLink({ href, variant = 'ghost', children, ...rest }: { href: string; variant?: 'primary' | 'ghost'; children: React.ReactNode } & Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & { 'data-testid'?: string }) {
  const C = variant === 'primary' ? PrimaryCta : GhostCta
  return (
    <Link href={href} passHref legacyBehavior>
      <C {...rest}>{children}</C>
    </Link>
  )
}

export const phaseIsProtective = (p: LockPhase) => p === 'locked' || p === 'vesting'
